/*
 * LumenForge Marketplace — cart state.
 *
 * A single React context, localStorage-persisted, no external deps. Every line
 * item is keyed by Stripe `lookup_key` (product's price id, e.g.
 * `kanto_2to1_gbp`) so `POST /api/payments/checkout` can consume it directly.
 */
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "lumenforge.cart.v1";

function loadCart() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveCart(items) {
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* localStorage unavailable */ }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadCart);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { saveCart(items); }, [items]);

  // Reconcile cart-line prices against the live catalog on mount.
  //
  // Cart lines are frozen at the moment "Add to Cart" was clicked, but the
  // CMS can rotate a product's Stripe price at any time (e.g. Kanto moving
  // £9.99 → £0.99). Without this pass the drawer + checkout preview would
  // still show the stale amount, even though Stripe charges the live price
  // (the backend only sends `lookup_key` to Stripe — never our cached amount).
  //
  // We hit `/api/payments/products` once at boot, match every cart line by
  // its Stripe lookup_key, and rewrite `unit_amount` / `currency` in place.
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    if (!API) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/payments/products`);
        if (!res.ok) return;
        const data = await res.json();
        const priceByKey = {};
        for (const p of (data.products || [])) {
          for (const pr of (p.prices || [])) {
            priceByKey[pr.lookup_key] = { unit_amount: pr.unit_amount, currency: pr.currency };
          }
        }
        if (cancelled) return;
        setItems((prev) => {
          let changed = false;
          const next = prev.map((x) => {
            const live = priceByKey[x.lookup_key];
            if (live && (live.unit_amount !== x.unit_amount || live.currency !== x.currency)) {
              changed = true;
              return { ...x, unit_amount: live.unit_amount, currency: live.currency };
            }
            return x;
          });
          return changed ? next : prev;
        });
      } catch { /* offline — leave cart as-is */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const addItem = useCallback((item) => {
    // item shape: { lookup_key, chosen_version?, name, unit_amount, currency, cover_image }
    setItems((prev) => {
      const existing = prev.find(
        (x) => x.lookup_key === item.lookup_key &&
               (x.chosen_version || null) === (item.chosen_version || null)
      );
      if (existing) {
        // Digital products can't be bought multiple times — no-op if already in cart.
        return prev;
      }
      return [...prev, { ...item, quantity: 1 }];
    });
    setDrawerOpen(true);
  }, []);

  const removeItem = useCallback((lookup_key, chosen_version = null) => {
    setItems((prev) => prev.filter(
      (x) => !(x.lookup_key === lookup_key && (x.chosen_version || null) === (chosen_version || null))
    ));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const has = useCallback(
    (lookup_key, chosen_version = null) =>
      items.some(
        (x) => x.lookup_key === lookup_key &&
               (x.chosen_version || null) === (chosen_version || null)
      ),
    [items]
  );

  const subtotal = useMemo(
    () => items.reduce((sum, x) => sum + (x.unit_amount || 0) * (x.quantity || 1), 0),
    [items]
  );
  const currency = items[0]?.currency || "gbp";

  const value = useMemo(() => ({
    items, subtotal, currency,
    addItem, removeItem, clearCart, has,
    drawerOpen, openDrawer: () => setDrawerOpen(true), closeDrawer: () => setDrawerOpen(false),
  }), [items, subtotal, currency, addItem, removeItem, clearCart, has, drawerOpen]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
}

export function formatMoney(amount, currency = "gbp") {
  const n = (amount || 0) / 100;
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: currency.toUpperCase() }).format(n);
  } catch {
    return `£${n.toFixed(2)}`;
  }
}
